import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { CalendarBlank } from '@phosphor-icons/react';

/**
 * DatePickerDMY — Date picker that displays DD/MM/YYYY format.
 * 
 * Props:
 *   value: ISO date string "YYYY-MM-DD" or ""
 *   onChange: (isoString) => void — returns "YYYY-MM-DD" format
 *   className: optional additional classes
 *   placeholder: optional placeholder text
 *   data-testid: optional test id
 */
export default function DatePickerDMY({ value, onChange, className = '', placeholder = 'DD/MM/YYYY', onKeyDown, ...props }) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Sync display value from ISO value — but only when the input is NOT focused
  // so the user's in-progress typing is never overwritten by a prop update.
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) return;
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        setInputValue(`${parts[2]}/${parts[1]}/${parts[0]}`);
      }
    } else {
      setInputValue('');
    }
  }, [value]);

  const selectedDate = value ? new Date(value + 'T00:00:00') : undefined;

  const handleCalendarSelect = (date) => {
    if (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      onChange(`${y}-${m}-${d}`);
    } else {
      onChange('');
    }
    setOpen(false);
  };

  const handleInputChange = (e) => {
    // Phase 364 — Continuous-typing UX: auto-insert "/" after DD and MM so
    // the user can type "18052026" or "18/05/2026" interchangeably, and so
    // typing keeps flowing when the existing date is auto-selected on focus.
    const raw = e.target.value;
    const onlyDigitsAndSlashes = raw.replace(/[^\d/]/g, '');
    const digits = onlyDigitsAndSlashes.replace(/\//g, '').slice(0, 8);
    let masked = '';
    if (digits.length <= 2) masked = digits;
    else if (digits.length <= 4) masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    else masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    setInputValue(masked);
    // Allow empty (user clearing the field)
    if (masked === '') {
      onChange('');
      return;
    }
    // Try to parse DD/MM/YYYY when complete
    const match = masked.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      const year = parseInt(yyyy, 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
        onChange(`${yyyy}-${mm}-${dd}`);
      }
    }
    // If not matching a full date yet, do NOT reset — keep what the user typed
    // so they can continue editing. The parent's `value` stays at its previous
    // valid state until the user enters another valid DD/MM/YYYY.
  };

  // Phase 364 — Auto-select the entire value on focus so typing a fresh
  // date instantly replaces the existing one (no need to delete digit by
  // digit). Works for both keyboard and mouse focus.
  const handleInputFocus = (e) => {
    try { e.target.select(); } catch {}
  };

  const handleInputBlur = () => {
    // On blur, only re-sync if the user typed something invalid.
    // Valid formats (empty or DD/MM/YYYY matching the parsed value) are kept.
    if (!inputValue) return; // empty — leave it
    const match = inputValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return; // valid, keep it
    }
    // Invalid: snap back to the last valid `value` (or clear if none)
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) setInputValue(`${parts[2]}/${parts[1]}/${parts[0]}`);
    } else {
      setInputValue('');
    }
  };

  const handleClear = () => {
    onChange('');
    setInputValue('');
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="flex h-8 w-full border border-input bg-background px-2 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring pr-8 font-mono"
        data-testid={props['data-testid']}
        style={{ minWidth: '110px' }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute right-1 p-1 text-zinc-400 hover:text-zinc-600"
            tabIndex={-1}
          >
            <CalendarBlank size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-6 text-zinc-300 hover:text-zinc-500 text-xs"
          tabIndex={-1}
        >
          &times;
        </button>
      )}
    </div>
  );
}
